import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-button';
import './vitesse-schema-form';
import type { BackendClient } from '../bridge/backend-client';
import type { FormFieldSpec } from '../../core/bridge/connect-client';

/**
 * <vitesse-add-symbol-dialog> — the backend-driven Add Symbol flow for a
 * controller whose plugin advertises can_add_symbols. Renders the plugin's
 * `add_symbol_form` (typically symbol_id + an optional type enum the plugin
 * defines) via <vitesse-schema-form> and submits AddSymbols. The plugin is the
 * authoritative validator; a valid symbol shows up through the indexer. Emits
 * `added` on success, then closes.
 */
@customElement('vitesse-add-symbol-dialog')
export class VitesseAddSymbolDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property() gatewayId = '';
  @property() controllerId = '';
  @property() pluginId = '';
  @property({ attribute: false }) fields: FormFieldSpec[] = [];
  @property({ attribute: false }) backend?: BackendClient;

  @state() private values: Record<string, string> = {};
  @state() private errors: Record<string, string> = {};
  @state() private generalError = '';
  @state() private busy = false;

  static styles = css`
    .general-err {
      margin: 0 0 12px;
      font-size: 13px;
      color: var(--red);
    }
  `;

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) this.reset();
  }

  private reset(): void {
    this.values = Object.fromEntries(this.fields.map((f) => [f.key, f.default ?? '']));
    this.errors = {};
    this.generalError = '';
    this.busy = false;
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  }

  private async submit(): Promise<void> {
    if (!this.backend || this.busy) return;
    this.busy = true;
    this.errors = {};
    this.generalError = '';
    try {
      const res = await this.backend.api.addSymbols(this.gatewayId, this.pluginId, this.controllerId, [this.values]);
      const item = res.results[0];
      if (res.ok && item?.ok) {
        this.dispatchEvent(new CustomEvent('added', { detail: { symbolId: item.symbolId }, bubbles: true, composed: true }));
        this.close();
        return;
      }
      this.generalError = item?.error || res.error || 'Could not add the symbol.';
    } catch (err) {
      this.generalError = err instanceof Error ? err.message : String(err);
    } finally {
      this.busy = false;
    }
  }

  render() {
    return html`<vitesse-dialog .open=${this.open} heading="Add symbol" @dialog-close=${this.close}>
      ${this.generalError ? html`<p class="general-err">${this.generalError}</p>` : nothing}
      <vitesse-schema-form
        .fields=${this.fields}
        .values=${this.values}
        .errors=${this.errors}
        @form-change=${(e: CustomEvent) => (this.values = (e.detail as { values: Record<string, string> }).values)}
      ></vitesse-schema-form>
      <vitesse-button slot="footer" color="red" @click=${this.close}>Cancel</vitesse-button>
      <vitesse-button slot="footer" color="blue" ?disabled=${this.busy} @click=${this.submit}>
        ${this.busy ? 'Adding…' : 'Add'}
      </vitesse-button>
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-add-symbol-dialog': VitesseAddSymbolDialog;
  }
}
